import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, allArticles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = allArticles.find((a) => a.slug === slug);
  const currentCategory = article
    ? categories.find((c) => c.slug === article.category)
    : null;

  const relatedArticles = article
    ? allArticles
        .filter((a) => a.category === article.category && a.slug !== article.slug)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <main className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <p className="mt-4 text-foreground-500">文章未找到</p>
          <Link
            to="/"
            className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const catNames: Record<string, string> = {};
  categories.forEach((c) => { catNames[c.slug] = c.name; });

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorTitle,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '性欲社',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://sportsix.github.io/article/${article.slug}`,
    },
  };

  const faqJsonLd = article.faq && article.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  } : null;

  return (
    <>
      <SEOMeta
        title={`${article.title} - 性欲社`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},性欲社,两性健康`}
        canonicalUrl={`https://sportsix.github.io/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
      />

      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      {faqJsonLd && (
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      )}

      <main className="pt-24 pb-16 min-h-screen">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory?.name || '', to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <header className="mt-8 mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {currentCategory && (
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    {currentCategory.name}
                  </Link>
                )}
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-line text-primary-500 text-sm"></i>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-800 text-sm">{article.author}</p>
                    <p className="text-xs text-foreground-400">{article.authorTitle}</p>
                  </div>
                </div>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <article
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article FAQ */}
            {article.faq && article.faq.length > 0 && (
              <section className="mt-12 p-6 md:p-8 bg-background-100 rounded-lg border border-background-200/70">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
                  相关问答
                </h3>
                <div className="flex flex-col gap-4">
                  {article.faq.map((f, idx) => (
                    <div key={idx}>
                      <p className="text-sm font-medium text-foreground-800">
                        Q: {f.question}
                      </p>
                      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                        A: {f.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Share */}
            <div className="mt-10 pt-6 border-t border-background-200/70 flex items-center gap-4">
              <span className="text-sm text-foreground-500">分享：</span>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(`https://sportsix.github.io/article/${article.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-foreground-900 hover:text-background-50 transition-all cursor-pointer"
                aria-label="分享到 Twitter"
              >
                <i className="ri-twitter-x-line"></i>
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://sportsix.github.io/article/${article.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-foreground-900 hover:text-background-50 transition-all cursor-pointer"
                aria-label="分享到 Facebook"
              >
                <i className="ri-facebook-line"></i>
              </a>
              <a
                href={`https://service.weibo.com/share/share.php?title=${encodeURIComponent(article.title)}&url=${encodeURIComponent(`https://sportsix.github.io/article/${article.slug}`)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-foreground-900 hover:text-background-50 transition-all cursor-pointer"
                aria-label="分享到微博"
              >
                <i className="ri-weibo-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 mt-16">
            <div className="max-w-7xl mx-auto">
              <div className="border-t border-background-200/70 pt-12">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard
                      key={ra.slug}
                      slug={ra.slug}
                      title={ra.title}
                      excerpt={ra.excerpt}
                      image={ra.image}
                      author={ra.author}
                      date={ra.date}
                      readTime={ra.readTime}
                      category={ra.category}
                      categoryName={catNames[ra.category] || ''}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose-custom h3 {
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 600;
          color: oklch(var(--foreground-800));
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .prose-custom p {
          font-size: 1rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 1.25rem;
        }
        @media (max-width: 768px) {
          .prose-custom h2 { font-size: 1.25rem; }
          .prose-custom p { font-size: 0.9375rem; }
        }
      `}</style>
    </>
  );
}